// splatforest.js — THE SPLATS (forest-splats, w42 2026-11-03): the far forest past the sprite band as one
// soft Gaussian per measured crown (APP_FORESTVOL §4f). The owner on the Quest: the far volume reads as
// 4–8 m blocks on dense slopes; a crown drawn as its own small Gaussian is soft by nature (no march, no
// coarse box, no dither).
//
// One GL POINTS draw over the Forest's world-wide crown arrays (trees.js: xyz / scales / radius, filled
// as its CPK1 tiles land — no second fetch, no second decode): a crown is one vertex, its splat a
// screen-space anisotropic Gaussian (σx from the crown radius, σy from its height) in the point sprite.
// - Where: only over a 1024 m shell tile the volume draws (forestVolResident: drawn + its group installed) —
//   an exact replacement of the volume, never a fill of its loading holes.
// - Keep: the EXACT complement of the sprite's whole-tree hand-off (handoff.js hoSpriteKeep, the same
//   function on the same trunk distance + hash): a splat draws ⇔ a canopy tile is resident under the
//   trunk ∧ ¬hoSpriteKeep(t, thr, tile) ∧ t ≤ the reach. No dither anywhere (P3).
// - Colour + height BY CONSTRUCTION (P2): the crown's own cell of the resident canopy tile (forestvol's
//   indirection, CANOPY_LOOKUP_GLSL): its top = y0 + H (the canopy CanopyGen rasterised on the DRAWN
//   ground — the volume's own surface), its colour = A at the first covered level (≥ SHADE_COV, ≤ SHADE_UP
//   up, the volume's exhausted-hit rule) × uWhite, look.canopy sat + gain, the snow of B (snowWeight over
//   B.b, lit by E), then the ONE display curve + the scene fog — the volume's uniforms, the same objects.
// - Sub-pixel: σ clamped ≥ MIN_PX with alpha × the area ratio (Mip-Splatting's energy rule = the wire.js
//   coverage rule): a far crown fades by coverage, never flickers in and out.
// - Order: none (Morton within a tile, tiles as they landed): sorted vs unsorted measured MAD 0.0004 of
//   luma (w42 spike: same-coloured far crowns), so no per-frame sort and no worker.
// - No double draw: the volume discards a hit nearer than the reach over a crown tile the splats hold
//   (forestvol uSplat / uSplatCov) and skips a whole 4096 m group they hold (setForestVolSplat).
// Reach = min(live gensurf vol.splat_m (per class, renderconfig ceiling), vol.range_m (governed)); 0 = off
// (the volume as before). `?splat=0|1` forces it (the A/B); `__vr.splat(on)` the probe / seam-lab toggle.
import * as THREE from 'three';
import { HANDOFF, HANDOFF_GLSL } from './handoff.js';
import { CANOPY_LOOKUP_GLSL, SNOW_COL_GLSL, forestVolSplatLook, setForestVolSplat, forestVolResident, SPLAT_TILE_M } from './forestvol.js';
import { SHADE_COV, SHADE_UP } from './forestmarch.js';
import { CANOPY_N, H_Q } from './crowns.js';
import { SNOW_GLSL } from './season.js';
import { genLive, onGenChange } from './gensched.js';
import { VEG_LUMA } from './vegpaint.js';

export const MIN_PX = 0.7;       // the splat's σ floor (px): below it alpha carries the coverage
const ALPHA = 0.92;              // a crown's peak opacity (the Gaussian's centre)
const WANT_M = 128;              // re-ask the far crown tiles after this much movement
const f1 = (v) => (Number.isInteger(v) ? v.toFixed(1) : String(v));
const v3 = (a) => `vec3(${a.map((x) => x.toFixed(5)).join(', ')})`;

const VS = `
#define FV_N ${CANOPY_N}
#define FV_HQ ${f1(H_Q)}
precision highp usampler2DArray;
uniform highp usampler2DArray uH;
uniform highp sampler2DArray uA, uB;
uniform float uPx, uMin, uSig, uAlpha, uReach, uNear, uFade, uWhite, uGain, uSat, uSnowGain;
uniform vec3 uSky, uSun, uL;
uniform vec4 uSeason[2];
uniform sampler2D uVolCov;
${CANOPY_LOOKUP_GLSL}
${HANDOFF_GLSL}
${SNOW_GLSL}${SNOW_COL_GLSL}
in float aH, aR;
out vec4 vC;
out vec3 vS;
#ifdef USE_FOG
out float vFogDepth;
#endif
void hide() { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); gl_PointSize = 0.0; vC = vec4(0.0); vS = vec3(1.0); }
void main() {
  vec2 xz = position.xz;
  float t = length(xz - cameraPosition.xz);
  ivec4 T; float y0;
  // not landed (h 0) / out of the band / no canopy tile / the sprite keeps it: none
  ivec2 vq = ivec2(floor(xz / ${f1(SPLAT_TILE_M)}));
  bool vol = all(greaterThanEqual(vq, ivec2(0))) && all(lessThan(vq, textureSize(uVolCov, 0))) && texelFetch(uVolCov, vq, 0).r > 0.5;
  if (aH < 0.5 || !vol || t > uReach || t < uNear - uFade || !tileOf(ivec2(floor(xz / FV_IND)), T, y0) || hoSpriteKeep(t, hoTreeThr(xz), true)) { hide(); return; }
  ivec2 hc = ivec2(clamp(floor((xz - vec2(T.zw) * FV_IND) / texelM(T.y)), vec2(0.0), vec2(float(FV_N) - 1.0)));
  uint hv = texelFetch(uH, ivec3(hc, T.x), 0).r;
  if (hv == 0u) { hide(); return; }            // no canopy at its cell: the volume draws none either
  int lf = 0;
  for (int k = 0; k < ${SHADE_UP}; k++) { if (texelFetch(uA, ivec3(hc >> lf, T.x), lf).a >= ${f1(SHADE_COV)}) break; lf++; }
  vec4 A = texelFetch(uA, ivec3(hc >> lf, T.x), lf), B = texelFetch(uB, ivec3(hc >> lf, T.x), lf);
  vec3 col = A.rgb * uWhite;
  col = mix(vec3(dot(col, ${v3(VEG_LUMA)})), col, uSat) * uGain;
  float top = y0 + float(hv) / FV_HQ;
  vec3 E = B.r * uSky + uSun * (max(uL.y, 0.0) * B.g);
  col = mix(col, snowCol(E), snowWeight(top, 0.0, 0.0, B.b));
  float h = aH / 8.0, rm = aR * 0.2;
  float R = aR > 0.5 ? clamp(rm * 1.1, 0.8, 0.45 * h) : 0.22 * h;
  vec4 mv = modelViewMatrix * vec4(xz.x, top - 0.45 * h, xz.y, 1.0);
  gl_Position = projectionMatrix * mv;
  float ppm = projectionMatrix[1][1] * uPx * 0.5 / max(-mv.z, 1.0);
  vec2 s = vec2(0.5 * R, 0.24 * h) * ppm * uSig, c = max(s, vec2(uMin));
  float ps = min(ceil(6.0 * max(c.x, c.y)) + 1.0, 255.0);
  vC = vec4(col, uAlpha * (s.x * s.y) / (c.x * c.y));
  vS = vec3(c, ps);
  gl_PointSize = ps;
#ifdef USE_FOG
  vFogDepth = -mv.z;
#endif
}`;

const FS = `
layout(location = 0) out highp vec4 pc_fragColor;
#define gl_FragColor pc_fragColor
in vec4 vC;
in vec3 vS;
#include <fog_pars_fragment>
void main() {
  vec2 q = (gl_PointCoord - 0.5) * vS.z / vS.xy;
  float a = vC.a * exp(-0.5 * dot(q, q));
  if (a < 1.0 / 255.0) discard;
  gl_FragColor = vec4(vC.rgb, 1.0);
#include <tonemapping_fragment>
#include <colorspace_fragment>
#include <fog_fragment>
  gl_FragColor = vec4(gl_FragColor.rgb * a, a);
}`;

// ?splat=0|1 (the A/B): null = the class's tunable
function splatParam() {
  try { const v = new URLSearchParams(globalThis.location ? location.search : '').get('splat'); return v === '0' ? 0 : v === '1' ? 1 : null; } catch { return null; }
}

// the effective reach (m) for a live view + the forced switch (null / 0 / 1)
export function splatReach(live, force) {
  const vol = live && live.vol;
  if (!vol) return 0;
  const range = vol.range_m || 0;
  if (force === 0) return 0;
  if (force === 1) return range;
  return Math.max(0, Math.min(vol.splat_m || 0, range));
}

export class SplatForest {
  constructor(forest, opt) {
    this.F = forest;
    this.look = forestVolSplatLook();
    this.force = splatParam();
    this.reach = 0;
    this.warm = false;
    const ix = forest.tiles, n = ix.count;
    let nx = 1, nz = 1;
    for (const t of ix.map.values()) { nx = Math.max(nx, t.i + 1); nz = Math.max(nz, t.j + 1); }
    this.cov = new THREE.DataTexture(new Uint8Array(nx * nz), nx, nz, THREE.RedFormat, THREE.UnsignedByteType);
    this.cov.minFilter = this.cov.magFilter = THREE.NearestFilter;
    this.cov.generateMipmaps = false;
    this.cov.needsUpdate = true;
    this.covN = [nx, nz];
    this.vcov = new THREE.DataTexture(new Uint8Array(nx * nz), nx, nz, THREE.RedFormat, THREE.UnsignedByteType);
    this.vcov.minFilter = this.vcov.magFilter = THREE.NearestFilter;
    this.vcov.generateMipmaps = false;
    this.vcov.needsUpdate = true;
    this._vsig = '';
    this.done = new Set();        // 'i_j' crown tiles uploaded
    const geo = new THREE.BufferGeometry();
    this.pos = new THREE.BufferAttribute(forest.xyz, 3);
    this.aH = new THREE.BufferAttribute(forest.scales, 1);
    this.aR = new THREE.BufferAttribute(forest.radius, 1);
    geo.setAttribute('position', this.pos);
    geo.setAttribute('aH', this.aH);
    geo.setAttribute('aR', this.aR);
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e9);
    this.U = {
      ...THREE.UniformsUtils.clone(THREE.UniformsLib.fog), ...this.look.uniforms,
      uPx: { value: 720 }, uMin: { value: MIN_PX }, uSig: { value: 1 }, uAlpha: { value: ALPHA }, uReach: { value: 0 }, uVolCov: { value: this.vcov }, uNear: HANDOFF.uNear, uFade: HANDOFF.uFade,
    };
    this.mat = new THREE.ShaderMaterial({
      name: 'forest-splat', glslVersion: THREE.GLSL3, vertexShader: VS, fragmentShader: FS, uniforms: this.U, fog: true,
      transparent: true, depthWrite: false, depthTest: true,
      blending: THREE.CustomBlending, blendEquation: THREE.AddEquation,
      blendSrc: THREE.OneFactor, blendDst: THREE.OneMinusSrcAlphaFactor,
      blendSrcAlpha: THREE.OneFactor, blendDstAlpha: THREE.OneMinusSrcAlphaFactor,
    });
    this.mesh = new THREE.Points(geo, this.mat);
    this.mesh.name = 'forest-splats';
    this.mesh.frustumCulled = false;
    this.mesh.matrixAutoUpdate = false;
    this.mesh.renderOrder = 1;
    this.mesh.visible = false;
    this.mesh.onBeforeRender = (renderer) => {
      this.look.sync();
      const rt = renderer.getRenderTarget();
      this.U.uPx.value = rt ? rt.height : renderer.getDrawingBufferSize(_v2).y;
    };
    opt.scene.add(this.mesh);
    this._at = null;
    this._dirty = true;
    onGenChange(() => { this._dirty = true; });
    this.stats = { tiles: 0, crowns: 0, vol: 0 };
  }

  // the switch (null = the class's tunable, 0 off, 1 on): applied now (the seam lab's arm toggles it around a render)
  // 'warm': the far crown tiles are asked + uploaded as if on, nothing drawn (an A/B's other arm)
  setForce(f) {
    if (f && typeof f === 'object') {   // the probe's shape knobs: { sig (σ scale), alpha (peak) }
      if (Number.isFinite(f.sig)) this.U.uSig.value = f.sig;
      if (Number.isFinite(f.alpha)) this.U.uAlpha.value = f.alpha;
      return;
    }
    if (f === 'warm') this.warm = true;
    else this.force = f === 0 || f === 1 ? f : null;
    this._dirty = true;
    this._apply(genLive());
  }

  _apply(live) {
    this._dirty = false;
    const r = splatReach(live, this.force);
    this.reach = r;
    this.U.uReach.value = r;
    this.mesh.visible = r > 0;
    setForestVolSplat(r > 0 ? { reach: r, tex: this.cov, holds: (gx, gz, gm) => this.holds(gx, gz, gm) } : null);
  }

  // a shell group (gx, gz of gm m) is wholly the splats': every crown tile of it that has crowns is in
  holds(gx, gz, gm) {
    const k = gm / SPLAT_TILE_M, ix = this.F.tiles.map;
    for (let j = gz * k; j < (gz + 1) * k; j++) {
      for (let i = gx * k; i < (gx + 1) * k; i++) {
        const key = i + '_' + j;
        if (ix.has(key) && !this.done.has(key)) return false;
      }
    }
    return true;
  }

  // once a tick: the live reach, the far crown tiles asked (bulk, the Forest's own VegTiles), the landed
  // tiles' ranges uploaded and marked in the cover grid
  tick(pos) {
    const live = genLive();
    if (!live || !pos) return;
    if (this._dirty) this._apply(live);
    const want = this.warm ? Math.max(this.reach, splatReach(live, 1)) : this.reach;
    if (!(want > 0)) return;
    const F = this.F;
    if (!this._at || Math.hypot(pos.x - this._at.x, pos.z - this._at.z) >= WANT_M) {
      this._at = { x: pos.x, z: pos.z };
      F.tiles.want(pos.x, pos.z, want + SPLAT_TILE_M, 'bulk');
    }
    let moved = false;
    for (const [key, t] of F.tiles.map) {
      if (this.done.has(key) || !F.byChunk.has(key)) continue;
      this.done.add(key);
      for (const [a, w] of [[this.pos, 3], [this.aH, 1], [this.aR, 1]]) { a.addUpdateRange(t.start * w, t.n * w); a.needsUpdate = true; }
      this.cov.image.data[t.j * this.covN[0] + t.i] = 255;
      moved = true;
      this.stats.tiles++; this.stats.crowns += t.n;
    }
    this._volCov();
    if (moved) { this.cov.needsUpdate = true; if (this.reach > 0) setForestVolSplat({ reach: this.reach, tex: this.cov, holds: (gx, gz, gm) => this.holds(gx, gz, gm) }); }
  }

  // the volume's resident 1024 m tiles → uVolCov (re-uploaded only when the set changed)
  _volCov() {
    const [nx, nz] = this.covN, ks = [];
    forestVolResident((i, j) => { if (i >= 0 && j >= 0 && i < nx && j < nz) ks.push(j * nx + i); });
    ks.sort((a, b) => a - b);
    const sig = ks.join(',');
    if (sig === this._vsig) return;
    this._vsig = sig;
    const a = this.vcov.image.data;
    a.fill(0);
    for (const k of ks) a[k] = 255;
    this.vcov.needsUpdate = true;
    this.stats.vol = ks.length;
  }

  state() { return { reach: this.reach, force: this.force, warm: this.warm, ...this.stats, drawn: this.mesh.visible }; }
}
const _v2 = new THREE.Vector2();

let SF = null;
// main.js, right after the Forest: null without crowns or a volume (the canopy lookup lives there)
export function installSplatForest(forest, opt) {
  if (SF || !forest || !forest.tiles || !forestVolSplatLook()) return SF;
  SF = new SplatForest(forest, opt);
  return SF;
}
export function splatForestTick(pos) { if (SF) SF.tick(pos); }
export function splatForest() { return SF; }
